import React, { useState } from 'react';
import { Box, Paper, Typography, useTheme, useMediaQuery } from '@mui/material';
import { useTaskTemplateStore } from '../store/taskTemplateStore';
import { caseStages } from '../data/caseStages';
import { TaskDialog } from '../components/cases/TaskDialog';
import { Task } from '../types';
import { TaskTemplateHeader } from '../components/task-templates/TaskTemplateHeader';
import { TaskTemplateList } from '../components/task-templates/TaskTemplateList';

export const TaskTemplates: React.FC = () => {
  const [selectedStage, setSelectedStage] = useState(caseStages[0].id);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | undefined>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const { getTemplatesForStage, updateTemplates } = useTaskTemplateStore();
  const currentTasks = getTemplatesForStage(selectedStage);
  const currentStage = caseStages.find(s => s.id === selectedStage);

  const handleTaskSave = (taskData: Partial<Task>) => {
    const tasks = [...currentTasks];
    
    if (selectedTask) {
      // Update existing task
      const index = tasks.findIndex(t => t.id === selectedTask.id);
      if (index !== -1) {
        tasks[index] = { 
          ...selectedTask, 
          ...taskData,
          assignedTo: '', // Clear assignee from template
        };
      }
    } else {
      // Add new task
      const newTask: Task = {
        id: `template-${Date.now()}`,
        title: taskData.title || '',
        description: taskData.description || '',
        assignedTo: '', // No default assignee
        dueDate: taskData.dueDate || new Date().toISOString(),
        status: 'pending',
        priority: taskData.priority as Task['priority'] || 'medium',
        stageId: selectedStage,
      };
      tasks.push(newTask);
    }

    updateTemplates(selectedStage, tasks);
    setTaskDialogOpen(false);
    setSelectedTask(undefined);
  };

  const handleDeleteTask = (taskId: string) => {
    const tasks = currentTasks.filter(t => t.id !== taskId);
    updateTemplates(selectedStage, tasks);
  };

  const handleEditTask = (task: Task) => {
    setSelectedTask(task);
    setTaskDialogOpen(true);
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 4 }}>Task Templates</Typography>

      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <TaskTemplateHeader
          stages={caseStages}
          selectedStage={selectedStage}
          onStageChange={setSelectedStage}
          onAddTemplate={() => setTaskDialogOpen(true)}
          currentStageName={currentStage?.name || ''}
          isMobile={isMobile}
        />

        <TaskTemplateList
          tasks={currentTasks}
          onEditTask={handleEditTask}
          onDeleteTask={handleDeleteTask}
          isMobile={isMobile}
        />
      </Paper>

      <TaskDialog
        open={taskDialogOpen}
        onClose={() => {
          setTaskDialogOpen(false);
          setSelectedTask(undefined);
        }}
        onSave={handleTaskSave}
        task={selectedTask}
        stageId={selectedStage}
        hideAssignment
        isTemplate
      />
    </Box>
  );
};